import { LitElement, css, html } from 'lit';
import { customElement } from 'lit/decorators.js';
import { i18n } from '../i18n/nl';

/**
 * `<embrace-self-fix-prompt>` — ja/nee-vraag onderaan een AAVA-chat die de
 * AI-assistent tijdens het gesprek heeft opgelost (`outcome: Resolved`).
 *
 * Kaartje in dezelfde stijl als `<embrace-rating-card>`; wordt door
 * `<embrace-message-list>` in een `.msg-row.bot`-container met avatar +
 * meta-regel gewikkeld (zie `renderSelfFixRow`) zodat de vraag in de
 * chat-stream staat en leest als een volgend bericht van de assistent.
 *
 * Alleen rendering — de business-rules (wanneer tonen, wat gebeurt er op
 * klik) leven in de reducer/controller. Events naar parent:
 *   - `self-fix-confirm` — "Ja, opgelost" → controller sluit de chat.
 *   - `self-fix-decline` — "Nee, niet opgelost" → excuus + redirect naar de RR-pagina.
 */
@customElement('embrace-self-fix-prompt')
export class EmbraceSelfFixPrompt extends LitElement {
  static override styles = css`
    :host {
      display: block;
    }

    .card {
      background: #fff;
      border: 1px solid #e6e7ea;
      border-radius: 14px;
      padding: 16px 18px;
      box-shadow: 0 1px 2px rgba(12, 28, 71, 0.04);
    }

    .intro {
      font: 400 13.5px/1.45 var(--ec-font-family);
      color: var(--ec-text);
      margin: 0 0 12px;
    }

    .actions {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      gap: 8px;
      margin-top: 4px;
    }
    .btn {
      padding: 8px 16px;
      font: 600 13px/1.2 var(--ec-font-family);
      border-radius: 8px;
      cursor: pointer;
      transition:
        background 120ms ease,
        border-color 120ms ease,
        color 120ms ease;
    }
    .btn.confirm {
      background: var(--ec-primary);
      color: var(--ec-on-primary);
      border: 0;
    }
    .btn.confirm:hover {
      background: var(--ec-primary-hover);
    }
    /* Secundair: outline i.p.v. fill, zodat "Ja" de primaire actie blijft. */
    .btn.decline {
      background: transparent;
      color: var(--ec-text-muted);
      border: 1px solid var(--ec-border-strong);
    }
    .btn.decline:hover {
      background: var(--ec-surface-sunken);
      color: var(--ec-text);
    }
    .btn:focus-visible {
      outline: var(--ec-focus-ring-width) solid var(--ec-focus-ring);
      outline-offset: var(--ec-focus-ring-offset);
    }
  `;

  override render() {
    return html`
      <div class="card" part="card">
        <p class="intro">${i18n.aava.selfFix.question}</p>
        <div class="actions">
          <button
            type="button"
            class="btn decline"
            @click=${this.onDecline}
          >
            ${i18n.aava.selfFix.decline}
          </button>
          <button
            type="button"
            class="btn confirm"
            @click=${this.onConfirm}
          >
            ${i18n.aava.selfFix.confirm}
          </button>
        </div>
      </div>
    `;
  }

  private onConfirm = () => {
    this.dispatchEvent(
      new CustomEvent('self-fix-confirm', { bubbles: true, composed: true }),
    );
  };

  private onDecline = () => {
    this.dispatchEvent(
      new CustomEvent('self-fix-decline', { bubbles: true, composed: true }),
    );
  };
}

declare global {
  interface HTMLElementTagNameMap {
    'embrace-self-fix-prompt': EmbraceSelfFixPrompt;
  }
}
